/**
 * Gemeinsamer Zustand für Saison-Dekoration (Effekt-Ebene, Ameise mit Hut, Dashboard-Banner).
 * Quelle: GET /api/seasonal-effect/active (Admin → Event-System → Saison-Effekte) bzw. Admin-Vorschau.
 */
import { createRoot, createSignal } from 'solid-js';
import apiClient from './api';

export type EffectName = 'snow' | 'leaves' | 'sparks' | 'fireworks' | 'blossom';
export interface Season { name: EffectName; intensity: number }

export const SEASON_THEME: Record<EffectName, { emojis: string[]; gradient: string; glow: string; chipKey: string }> = {
  sparks:    { emojis: ['🦇', '🎃', '👻', '🕸️'], gradient: 'linear-gradient(135deg,#fb923c 0%,#f97316 40%,#a855f7 100%)', glow: 'rgba(249,115,22,0.35)', chipKey: 'season.sparks' },
  snow:      { emojis: ['⛄', '❄️', '🎄', '🎁'], gradient: 'linear-gradient(135deg,#67e8f9 0%,#e0f2fe 55%,#a5b4fc 100%)', glow: 'rgba(125,211,252,0.35)', chipKey: 'season.snow' },
  fireworks: { emojis: ['🎆', '🥂', '🎉', '✨'], gradient: 'linear-gradient(135deg,#fbbf24 0%,#f472b6 55%,#a78bfa 100%)', glow: 'rgba(251,191,36,0.35)', chipKey: 'season.fireworks' },
  leaves:    { emojis: ['🍁', '🍂', '🌰', '🍄'], gradient: 'linear-gradient(135deg,#fbbf24 0%,#f59e0b 45%,#b45309 100%)', glow: 'rgba(245,158,11,0.35)', chipKey: 'season.leaves' },
  blossom:   { emojis: ['🌸', '🦋', '🐝', '🌷'], gradient: 'linear-gradient(135deg,#f9a8d4 0%,#fbcfe8 45%,#86efac 100%)', glow: 'rgba(249,168,212,0.35)', chipKey: 'season.blossom' },
};

const STORAGE_KEY = 'seasonal-effects';
const readEnabled = () => { try { return localStorage.getItem(STORAGE_KEY) !== 'off'; } catch { return true; } };

const store = createRoot(() => {
  const [live, setLive] = createSignal<Season | null>(null);
  const [preview, setPreview] = createSignal<Season | null>(null);
  const [enabled, setEnabled] = createSignal(readEnabled());
  return { live, setLive, preview, setPreview, enabled, setEnabled };
});

let started = false;
let previewTimer: number | undefined;

async function load() {
  try {
    const res = await apiClient.get<{ success: boolean; data: { effect: string; intensity: number } | null }>('/seasonal-effect/active');
    const d = res.data?.data;
    store.setLive(d ? { name: d.effect as EffectName, intensity: Math.min(3, Math.max(0.3, Number(d.intensity) || 1)) } : null);
  } catch { /* kein Effekt */ }
}

function start() {
  if (started || typeof window === 'undefined') return;
  started = true;
  load();
  setInterval(load, 10 * 60 * 1000);
  window.addEventListener('seasonal-effects-changed', () => store.setEnabled(readEnabled()));
  window.addEventListener('seasonal-effect-preview', (e) => {
    const d = (e as CustomEvent).detail as { effect: EffectName; intensity?: number };
    store.setPreview({ name: d.effect, intensity: d.intensity || 1 });
    clearTimeout(previewTimer);
    previewTimer = window.setTimeout(() => store.setPreview(null), 15000);
  });
}

export function seasonalEffectsEnabled(): boolean { return readEnabled(); }
export function setSeasonalEffectsEnabled(on: boolean) {
  try { localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off'); } catch {}
  window.dispatchEvent(new Event('seasonal-effects-changed'));
}

/** Aktuelle Saison (Vorschau hat Vorrang und ignoriert den Spieler-Schalter), sonst null */
export function useSeason() {
  start();
  const season = () => store.preview() || (store.enabled() ? store.live() : null);
  return { season, isPreview: () => !!store.preview() };
}
